/**
 * Project scroll hint — shows a floating down-chevron at the bottom of the
 * project prose column when it overflows and the user hasn't reached the
 * end. Watches scroll position and content size so the indicator stays
 * accurate as fonts load, the viewport resizes, or the user scrolls.
 * CSP-safe: method refs only.
 */
export default function projectScrollHint() {
    return {
        canScrollDown: false,
        _scroller: null,
        _resizeObserver: null,
        _rafId: 0,

        init() {
            this._scroller = this.$refs.scroller;
            if (!this._scroller) {
                return;
            }

            this._onScroll = this._onScroll.bind(this);
            this._scroller.addEventListener('scroll', this._onScroll, { passive: true });

            if (typeof ResizeObserver !== 'undefined') {
                this._resizeObserver = new ResizeObserver(() => this._scheduleMeasure());
                this._resizeObserver.observe(this._scroller);
                for (const child of this._scroller.children) {
                    this._resizeObserver.observe(child);
                }
            }

            this._scheduleMeasure();
        },

        destroy() {
            if (this._scroller && this._onScroll) {
                this._scroller.removeEventListener('scroll', this._onScroll);
            }
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
                this._resizeObserver = null;
            }
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
                this._rafId = 0;
            }
        },

        scrollDown() {
            if (!this._scroller) {
                return;
            }
            const step = Math.max(120, Math.floor(this._scroller.clientHeight * 0.8));
            this._scroller.scrollBy({ top: step, behavior: 'smooth' });
        },

        _onScroll() {
            this._scheduleMeasure();
        },

        _scheduleMeasure() {
            if (this._rafId) {
                return;
            }
            this._rafId = requestAnimationFrame(() => {
                this._rafId = 0;
                this._measure();
            });
        },

        _measure() {
            const el = this._scroller;
            if (!el) {
                this.canScrollDown = false;
                return;
            }
            const remaining = el.scrollHeight - el.clientHeight - el.scrollTop;
            // 8px tolerance so the arrow disappears as the user lands at the bottom.
            this.canScrollDown = remaining > 8;
        },
    };
}
